/**
 * Turning what a relative typed into a link that opens their profile.
 *
 * Nobody in this family will type a canonical URL. They will paste the address
 * bar, or the share-sheet link with its tracking tail, or they will write
 * `@sarah.katz`, or just `sarah.katz`. All four mean the same person, so all
 * four are accepted and reduced to the one thing a link needs: the handle.
 *
 * The rule for rejecting is the same as `lib/phone`: only refuse what plainly
 * cannot work. A handle we pass through that turns out to be wrong lands the
 * viewer on a "no such profile" page, which is recoverable and obvious. A
 * handle we refuse never becomes a button at all, and the person who typed it
 * has no idea why — which is worse.
 */

/** Hosts that mean "this is an Instagram link", including the share-sheet shortener. */
const INSTAGRAM_HOSTS = ['instagram.com', 'instagr.am', 'ig.me'];
const FACEBOOK_HOSTS = ['facebook.com', 'fb.com', 'fb.me', 'm.facebook.com'];

/** The first path segment of a URL, or undefined when there is nothing in the path. */
function firstSegment(url: URL): string | undefined {
  const seg = url.pathname.split('/').filter(Boolean)[0];
  return seg ? decodeURIComponent(seg) : undefined;
}

/**
 * What a relative typed, reduced to a handle.
 *
 * Anything with a host we recognise is read as a URL and its first path segment
 * taken; anything else is treated as the handle itself, with a leading `@` and
 * any stray slashes trimmed. A URL on a host we do *not* recognise is refused —
 * a link to somebody's blog is not an Instagram profile, and turning it into
 * `instagram.com/https:` helps nobody.
 */
function handleFrom(raw: string | undefined, hosts: string[]): string | undefined {
  const text = raw?.trim();
  if (!text) return undefined;
  const looksLikeUrl = /^(https?:)?\/\//i.test(text) || /^[\w.-]+\.[a-z]{2,}\//i.test(text);
  if (looksLikeUrl) {
    let url: URL;
    try {
      url = new URL(text.startsWith('http') ? text : `https://${text.replace(/^\/\//, '')}`);
    } catch {
      return undefined;
    }
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    if (!hosts.includes(host)) return undefined;
    // facebook.com/profile.php?id=123 — the numeric id is the handle in that shape.
    const id = url.searchParams.get('id');
    const seg = firstSegment(url);
    if (seg === 'profile.php' && id) return /^\d+$/.test(id) ? id : undefined;
    return seg;
  }
  return text.replace(/^@+/, '').replace(/^\/+|\/+$/g, '') || undefined;
}

/** Instagram handles: letters, digits, dots and underscores, up to 30. */
export function instagramLink(raw: string | undefined): string | undefined {
  const handle = handleFrom(raw, INSTAGRAM_HOSTS);
  if (!handle || !/^[A-Za-z0-9._]{1,30}$/.test(handle)) return undefined;
  return `https://instagram.com/${handle}`;
}

/**
 * Facebook is either a vanity name (letters, digits and dots) or the numeric id
 * a profile has before anyone sets one — and a numeric id has to go back through
 * `profile.php`, because facebook.com/<digits> is not a profile.
 */
export function facebookLink(raw: string | undefined): string | undefined {
  const handle = handleFrom(raw, FACEBOOK_HOSTS);
  if (!handle) return undefined;
  if (/^\d{5,}$/.test(handle)) return `https://facebook.com/profile.php?id=${handle}`;
  if (!/^[A-Za-z0-9.]{4,60}$/.test(handle)) return undefined;
  return `https://facebook.com/${handle}`;
}
